import { useMemo, useState } from "react";
import { api, type TipSummary } from "@/api/client";
import { loadGroupBySlug } from "@/lib/groups";
import { useAuth } from "@/hooks/useAuth";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

// Precomputed distribution stats — what `Distribution` renders. The global
// tab gets these straight from the server-side summary endpoint (bounded
// payload, 10s cache); the group tab computes them client-side from the
// member-filtered tip list.
type DistStats = {
  total: number;
  hPct: number;
  dPct: number;
  aPct: number;
  top: [string, number][]; // [["2:1", 41], ...] top 3 by count
};

function statsFromSummary(s: TipSummary): DistStats | null {
  if (!s.total) return null;
  return {
    total: s.total,
    hPct: Math.round((s.home / s.total) * 100),
    dPct: Math.round((s.draw / s.total) * 100),
    aPct: Math.round((s.away / s.total) * 100),
    top: s.dist.slice(0, 3).map((d) => [`${d.h}:${d.a}`, d.count]),
  };
}

function statsFromTips(tips: { home_tip: number; away_tip: number }[]): DistStats | null {
  if (!tips.length) return null;
  let h = 0, d = 0, a = 0;
  const counts: Record<string, number> = {};
  tips.forEach((t) => {
    if (t.home_tip > t.away_tip) h++;
    else if (t.home_tip < t.away_tip) a++;
    else d++;
    const k = `${t.home_tip}:${t.away_tip}`;
    counts[k] = (counts[k] ?? 0) + 1;
  });
  const total = tips.length;
  return {
    total,
    hPct: Math.round((h / total) * 100),
    dPct: Math.round((d / total) * 100),
    aPct: Math.round((a / total) * 100),
    top: Object.entries(counts).sort((x, y) => y[1] - x[1]).slice(0, 3),
  };
}

export function TipHeatmap({
  gameId,
  homeFlag,
  awayFlag,
  globalSummary,
}: {
  gameId: string;
  homeFlag: string;
  awayFlag: string;
  globalSummary: TipSummary;
}) {
  const { user } = useAuth();
  const [groupStats, setGroupStats] = useState<DistStats | null | "loading" | "untouched">("untouched");

  const globalStats = useMemo(() => statsFromSummary(globalSummary), [globalSummary]);

  // Lazy-load the group tab on first open. The legacy full-tips endpoint
  // is needed here (it's the only one carrying per-tip userIds for the
  // member filter) — keeping it off the mount path means the heavy
  // download only happens for the few users who actually open this tab,
  // not for every game-detail view during a live match.
  const loadGroupTab = async () => {
    if (!user || groupStats !== "untouched") return;
    setGroupStats("loading");
    try {
      const myGroups = (await api.groups.mine()) as any[];
      if (!myGroups.length) { setGroupStats(null); return; }
      const detailed = await Promise.all(myGroups.map((g) => loadGroupBySlug(g.slug).catch(() => null)));
      const userIds = new Set<string>();
      detailed.forEach((d) => d?.members.forEach((m) => userIds.add(m.user_id)));
      if (!userIds.size) { setGroupStats(null); return; }
      const allTips = (await api.tips.listForGame(gameId)) as any[];
      const filtered = allTips
        .filter((t: any) => userIds.has(t.userId))
        .map((t: any) => ({ home_tip: t.homeTip, away_tip: t.awayTip }));
      setGroupStats(statsFromTips(filtered));
    } catch {
      setGroupStats(null);
    }
  };

  return (
    <div className="bg-card rounded-lg shadow-card p-5">
      <h3 className="font-display font-bold text-base mb-3">🔥 Wer tippt was?</h3>
      <Tabs defaultValue="global" onValueChange={(v) => { if (v === "groups") loadGroupTab(); }}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="global">Alle Tipper</TabsTrigger>
          <TabsTrigger value="groups" disabled={!user}>Meine Gruppen</TabsTrigger>
        </TabsList>
        <TabsContent value="global" className="mt-3">
          <Distribution stats={globalStats} homeFlag={homeFlag} awayFlag={awayFlag} />
        </TabsContent>
        <TabsContent value="groups" className="mt-3">
          {groupStats === "untouched" || groupStats === "loading" ? (
            <div className="text-center text-sm text-muted-foreground py-4">Lade Gruppentipps …</div>
          ) : (
            <Distribution stats={groupStats} homeFlag={homeFlag} awayFlag={awayFlag} emptyMsg="Noch keine Tipps in deinen Gruppen." />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Distribution({
  stats,
  homeFlag,
  awayFlag,
  emptyMsg = "Noch keine Tipps abgegeben.",
}: { stats: DistStats | null; homeFlag: string; awayFlag: string; emptyMsg?: string }) {
  if (!stats) {
    return <div className="text-center text-sm text-muted-foreground py-4">{emptyMsg}</div>;
  }

  return (
    <div className="space-y-3">
      <div className="flex h-9 rounded-pill overflow-hidden text-xs font-bold">
        {stats.hPct > 0 && (
          <div style={{ width: `${stats.hPct}%` }} className="bg-accent text-accent-foreground flex items-center justify-center">
            {homeFlag} {stats.hPct}%
          </div>
        )}
        {stats.dPct > 0 && (
          <div style={{ width: `${stats.dPct}%` }} className="bg-muted text-muted-foreground flex items-center justify-center">
            X {stats.dPct}%
          </div>
        )}
        {stats.aPct > 0 && (
          <div style={{ width: `${stats.aPct}%` }} className="bg-secondary text-secondary-foreground flex items-center justify-center">
            {awayFlag} {stats.aPct}%
          </div>
        )}
      </div>

      <div className="space-y-1.5">
        <div className="text-[10px] uppercase font-bold text-muted-foreground tracking-wider">Top-Ergebnisse</div>
        {stats.top.map(([score, n]) => {
          const pct = Math.round(n / stats.total * 100);
          return (
            <div key={score} className="flex items-center gap-2">
              <div className="font-display font-bold text-sm tabular-nums w-10">{score}</div>
              <div className="flex-1 h-5 bg-muted rounded-pill overflow-hidden relative">
                <div
                  className="absolute inset-y-0 left-0 bg-accent/60 rounded-pill transition-all"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className="text-xs font-bold tabular-nums w-12 text-right text-muted-foreground">{pct}%</div>
            </div>
          );
        })}
      </div>

      <div className="text-[10px] text-muted-foreground text-center">{stats.total} Tipps gesamt</div>
    </div>
  );
}
